import { getCookie, setCookie } from "cookies-next"
import { type ReadonlyRequestCookies } from "next/dist/server/web/spec-extension/adapters/request-cookies"

export type TFeatureStatus = "enabled" | "disabled" | "unknown"

const COOKIE_DJ_UI_FEATURE = "dj_ui_feature"

/**
 * Retrieves the feature status of the DJ UI server.
 * @returns The feature status of the DJ UI server.
 */
export function getDJUIServerFeatureStatus() {
  const envValue = process.env.NEXT_PUBLIC_ENABLE_DJ_UI
  const envStatus: TFeatureStatus =
    envValue === "true"
      ? "enabled"
      : envValue === "unknown"
        ? "unknown"
        : "disabled"

  return envStatus
}

/**
 * Determines if the DJUI feature is enabled based on the environment status and cookie value.
 * @param cookiesJar - The cookies jar to use. It is only needed for server-side rendering.
 * @returns {boolean} Returns true if the DJUI feature is enabled, otherwise false.
 */
export function isDJUIEnabled(cookiesJar?: () => ReadonlyRequestCookies) {
  const envStatus: TFeatureStatus = getDJUIServerFeatureStatus()

  const cookieValue = cookiesJar
    ? getCookie(COOKIE_DJ_UI_FEATURE, { cookies: cookiesJar })
    : getCookie(COOKIE_DJ_UI_FEATURE)
  const cookieStatus: TFeatureStatus =
    cookieValue === "enabled"
      ? "enabled"
      : cookieValue === "disabled"
        ? "disabled"
        : "unknown"

  if (envStatus === "disabled") {
    return false
  }

  if (envStatus === "enabled") {
    return cookieStatus === "enabled" || cookieStatus === "unknown"
  }

  if (envStatus === "unknown") {
    return cookieStatus === "enabled"
  }

  return false
}

/**
 * Sets the value of the DJUI feature flag.
 *
 * @param value - The status of the feature flag.
 * @param cookiesJar - The cookies jar to use. It is only needed for server-side rendering.
 */
export function setDJUIFeature(
  value: TFeatureStatus,
  cookiesJar?: () => ReadonlyRequestCookies
) {
  setCookie(COOKIE_DJ_UI_FEATURE, value, {
    httpOnly: false,
    secure: true,
    sameSite: "lax",
    expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365),
    domain:
      process.env.NODE_ENV === "production" ? ".ringio.com" : ".localhost",
    ...(cookiesJar ? { cookies: cookiesJar } : {}),
  })
}
